import { DISTRICTS, DistrictInfo } from './mockData';

export interface KmlPolygon {
  id: string;
  name: string;
  // [lat, lng] pairs for Leaflet
  coordinates: [number, number][];
  centroid: [number, number];
  // Inferred district code (e.g. "D18")
  districtCode: string;
}

// Try to recover a district code from a placemark name like "D18" or "District 18".
function parseExplicitDistrict(name: string): string | null {
  const m1 = name.trim().match(/^D(\d{1,2})$/i);
  if (m1) return `D${parseInt(m1[1], 10).toString().padStart(2, '0')}`;
  const m2 = name.trim().match(/^District\s+(\d{1,2})$/i);
  if (m2) return `D${parseInt(m2[1], 10).toString().padStart(2, '0')}`;
  return null;
}

function centroidOf(points: [number, number][]): [number, number] {
  if (points.length === 0) return [0, 0];
  let lat = 0;
  let lng = 0;
  for (const [a, b] of points) {
    lat += a;
    lng += b;
  }
  return [lat / points.length, lng / points.length];
}

// Find the district whose center is closest to a polygon centroid.
function nearestDistrict([lat, lng]: [number, number]): DistrictInfo {
  let best = DISTRICTS[0];
  let bestDist = Number.POSITIVE_INFINITY;
  for (const d of DISTRICTS) {
    const [dLat, dLng] = d.center;
    const dist = (dLat - lat) ** 2 + (dLng - lng) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = d;
    }
  }
  return best;
}

export function parseKml(text: string): KmlPolygon[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'application/xml');
  const placemarks = Array.from(doc.getElementsByTagName('Placemark'));

  const polygons: KmlPolygon[] = [];
  placemarks.forEach((pm, idx) => {
    const nameEl = pm.getElementsByTagName('name')[0];
    const rawName = nameEl?.textContent?.trim() || `Polygon ${idx + 1}`;

    // Each placemark may have multiple <coordinates> nodes (multipolygon) — handle the first <outerBoundaryIs>
    const polyEls = Array.from(pm.getElementsByTagName('Polygon'));
    polyEls.forEach((polyEl, polyIdx) => {
      const outer = polyEl.getElementsByTagName('outerBoundaryIs')[0];
      const coordsEl = outer?.getElementsByTagName('coordinates')[0];
      const coordsText = coordsEl?.textContent?.trim();
      if (!coordsText) return;

      // KML coords are "lng,lat[,alt] lng,lat[,alt] ..."
      const points: [number, number][] = coordsText
        .split(/\s+/)
        .map((p) => p.split(',').map(Number))
        .filter((arr) => arr.length >= 2 && Number.isFinite(arr[0]) && Number.isFinite(arr[1]))
        .map(([lng, lat]) => [lat, lng] as [number, number]);

      if (points.length < 3) return;

      const centroid = centroidOf(points);
      const explicit = parseExplicitDistrict(rawName);
      const districtCode = explicit ?? nearestDistrict(centroid).code;

      polygons.push({
        id: `${idx}-${polyIdx}-${rawName}`,
        name: rawName,
        coordinates: points,
        centroid,
        districtCode,
      });
    });
  });

  return polygons;
}

let cache: KmlPolygon[] | null = null;
let inflight: Promise<KmlPolygon[]> | null = null;

export async function loadKml(url: string): Promise<KmlPolygon[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch KML: ${res.status}`);
    const text = await res.text();
    cache = parseKml(text);
    return cache;
  })();
  return inflight;
}
